import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
// =========================================
// =========================================

//  this file is in charge of coordinating
//  the dispatching of actions necessary
//  to interact with items via the api

// =========================================
// =========================================

import first from 'lodash/first';
import { CREATE_ITEM_CALL, CREATE_BULK_ITEMS_CALL, UPDATE_ITEM_CALL, PARENT_TYPE_ADDING_TO_BANK } from '@instructure/quiz-common';
import { addError } from '../../common/actions/alerts';
import { handleError, errorsAllFromValidation, processApiError, getOnErrorWithDelayedValidationMessage } from '../../common/api/helpers';
import Fetcher from '../../common/util/Fetcher';
import { handleItemsResponse } from '../../common/api/callHandlers';
import t from '@instructure/quiz-i18n/format-message';

// =====================
// =====================
//       CREATE
// =====================
// =====================

export function getItem(itemId) {
  var url = "/api/items/".concat(itemId);
  return function (dispatch, getState) {
    var fetcher = new Fetcher();
    return fetcher.get(url).then(function (itemData) {
      dispatch(handleItemsResponse([itemData]));
      return itemData;
    });
  };
}
export function createItem(item, parentId, parentType) {
  var parentItemId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  var resource = parentType === 'bank' ? 'banks' : 'quizzes';
  var url = "/api/".concat(resource, "/").concat(parentId, "/items");
  var itemParams = {
    feedback: item.feedback,
    interaction_data: item.interactionData,
    interaction_type_id: item.interactionType,
    item_body: item.itemBody,
    outcome_alignment_set_guid: item.outcomeAlignmentSetGuid,
    parent_item_id: parentItemId,
    properties: item.properties,
    scoring_algorithm: item.getScoringAlgorithm(),
    scoring_data: item.scoringData,
    title: item.title,
    user_response_type: first(item.getInteractionType().userResponseTypeOptions),
    stimulus_id: item.stimulusId,
    calculator_type: item.calculatorType,
    answer_feedback: item.answerFeedback
  };
  var requestBody = JSON.stringify({
    item: itemParams
  });
  return function (dispatch, getState) {
    var errorMessage = t('Failed to create item.');
    var onError = function onError(_ref) {
      var response = _ref.response;
      return /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _yield$response$json, errors;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!(response && response.status === 422)) {
                _context.next = 12;
                break;
              }
              _context.prev = 1;
              _context.next = 4;
              return response.json();
            case 4:
              _yield$response$json = _context.sent;
              errors = _yield$response$json.errors;
              if (!errorsAllFromValidation(errors)) {
                _context.next = 8;
                break;
              }
              return _context.abrupt("return");
            case 8:
              _context.next = 12;
              break;
            case 10:
              _context.prev = 10;
              _context.t0 = _context["catch"](1);
            case 12:
              return _context.abrupt("return", dispatch(addError(errorMessage)));
            case 13:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[1, 10]]);
      }));
    };
    if (parentType === PARENT_TYPE_ADDING_TO_BANK) {
      onError = getOnErrorWithDelayedValidationMessage(dispatch, t('Item could not be added to bank due to invalid input data.'), t('Item could not be added to bank.'));
    }
    if (parentType === 'bank') {
      onError = handleError(dispatch, errorMessage);
    }
    var fetcher = new Fetcher({
      callType: CREATE_ITEM_CALL,
      cloneResponseForError: true,
      onError: onError
    });
    var createItemParams = {
      body: requestBody
    };

    // Although the fetcher has an onError method, it's specifically for dispatching
    // redux actions (e.g. to show notifications).
    // API validation error handling doesn't go through redux, so we catch the
    // fetcher error separately below and add the necessary formatted data to it
    return fetcher.post(url, createItemParams).then(function (itemData) {
      dispatch(handleItemsResponse([itemData]));
      return itemData;
    })["catch"](processApiError);
  };
}

/**
 * Creates multiple items in bulk. The input data must consist of question groups and questions
 * using a Learnosity format.
 *
 * @param {{
 *   question_type: string,
 *   questions: unknown[]
 * }} items
 * @param {object} options - Options for the bulk creation.
 * @param {string} options.parentId - The ID of the parent resource (quiz or bank).
 * @param {string} options.parentType - The type of the parent resource, either 'bank' or 'quiz'.
 * @returns {function} - A thunk function that dispatches the action to create items.
 */
export function createBulkItems(items, _ref3) {
  var parentId = _ref3.parentId,
    parentType = _ref3.parentType;
  var resource = parentType === 'bank' ? 'banks' : 'quizzes';
  var url = "/api/".concat(resource, "/").concat(parentId, "/items/bulk");
  var body = {
    body: JSON.stringify({
      items: items
    })
  };
  return /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(dispatch) {
      var fetcher;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            fetcher = new Fetcher({
              callType: CREATE_BULK_ITEMS_CALL,
              cloneResponseForError: true,
              onError: function onError(_ref5) {
                var response = _ref5.response;
                return /*#__PURE__*/function () {
                  var _ref6 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(dispatch) {
                    var _yield$response$json2, errors;
                    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                      while (1) switch (_context2.prev = _context2.next) {
                        case 0:
                          if (!(response && response.status === 422)) {
                            _context2.next = 12;
                            break;
                          }
                          _context2.prev = 1;
                          _context2.next = 4;
                          return response.json();
                        case 4:
                          _yield$response$json2 = _context2.sent;
                          errors = _yield$response$json2.errors;
                          if (!errorsAllFromValidation(errors)) {
                            _context2.next = 8;
                            break;
                          }
                          return _context2.abrupt("return");
                        case 8:
                          _context2.next = 12;
                          break;
                        case 10:
                          _context2.prev = 10;
                          _context2.t0 = _context2["catch"](1);
                        case 12:
                          return _context2.abrupt("return", dispatch(addError(t('Failed to create items.'))));
                        case 13:
                        case "end":
                          return _context2.stop();
                      }
                    }, _callee2, null, [[1, 10]]);
                  }));
                  return function (_x2) {
                    return _ref6.apply(this, arguments);
                  };
                }();
              }
            });
            return _context3.abrupt("return", fetcher.post(url, body).then(function (_ref7) {
              var items = _ref7.items,
                errors = _ref7.errors;
              dispatch(handleItemsResponse(items));
              return {
                items: items,
                errors: errors
              };
            })["catch"](processApiError));
          case 2:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }));
    return function (_x) {
      return _ref4.apply(this, arguments);
    };
  }();
}

// =====================
// =====================
//       UPDATE
// =====================
// =====================

function updateItemParams(item) {
  return {
    item_body: item.itemBody,
    title: item.title,
    scoring_data: item.scoringData,
    scoring_algorithm: item.getScoringAlgorithm(),
    interaction_data: item.interactionData,
    properties: item.properties,
    outcome_alignment_set_guid: item.outcomeAlignmentSetGuid,
    feedback: item.feedback,
    calculator_type: item.calculatorType,
    answer_feedback: item.answerFeedback
  };
}
export function updateItem(item, parentId, parentType) {
  var resource = parentType === 'bank' ? 'banks' : 'quizzes';
  var url = "/api/".concat(resource, "/").concat(parentId, "/items/").concat(item.id);
  var reqBody = JSON.stringify({
    item: updateItemParams(item)
  });
  return function (dispatch, getState) {
    var updateItemParams = {
      body: reqBody
    };
    var fetcher = new Fetcher({
      callType: UPDATE_ITEM_CALL,
      cloneResponseForError: true,
      onError: function onError(_ref8) {
        var response = _ref8.response;
        return /*#__PURE__*/function () {
          var _ref9 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4(dispatch) {
            var _yield$response$json3, errors;
            return _regeneratorRuntime.wrap(function _callee4$(_context4) {
              while (1) switch (_context4.prev = _context4.next) {
                case 0:
                  if (!(response && response.status === 422)) {
                    _context4.next = 14;
                    break;
                  }
                  _context4.prev = 1;
                  _context4.next = 4;
                  return response.json();
                case 4:
                  _yield$response$json3 = _context4.sent;
                  errors = _yield$response$json3.errors;
                  if (!(errors.base && errors.base.some(function (message) {
                    return message.includes('cannot update an immutable item');
                  }))) {
                    _context4.next = 8;
                    break;
                  }
                  return _context4.abrupt("return", dispatch(addError(t('This item has now been viewed by a student, so your edits were not saved. ' + 'Please refresh the page and try again.'))));
                case 8:
                  if (!errorsAllFromValidation(errors)) {
                    _context4.next = 10;
                    break;
                  }
                  return _context4.abrupt("return");
                case 10:
                  _context4.next = 14;
                  break;
                case 12:
                  _context4.prev = 12;
                  _context4.t0 = _context4["catch"](1);
                case 14:
                  return _context4.abrupt("return", dispatch(addError(t('Failed to update item.'))));
                case 15:
                case "end":
                  return _context4.stop();
              }
            }, _callee4, null, [[1, 12]]);
          }));
          return function (_x3) {
            return _ref9.apply(this, arguments);
          };
        }();
      }
    });
    return fetcher.patch(url, updateItemParams).then(function (itemData) {
      dispatch(handleItemsResponse([itemData]));
      return itemData;
    })["catch"](processApiError);
  };
}